import React, { useState, useEffect, useMemo } from "react";
import { entities, getDisplayMessage } from "@/api/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle2,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  FileText,
  Eye,
  Filter,
  Calendar,
  Trash2,
  Clock,
  AlertCircle,
  Paperclip,
  Search,
  Star,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
  ,
  UserCog,
  Loader2
} from "lucide-react";
import { format, differenceInDays, addDays, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { createResultadosEditalDeepLink, openPageInNewTab } from "@/utils";
import { parseDataCertame } from "@/lib/dashboardComercialUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AnexosEdital from "../components/analise/AnexosEdital"; // Imported AnexosEdital component
import { Alert, AlertDescription } from "@/components/ui/alert"; // Imported Alert for error display


const statusConfig = {
  pendente: {
    label: "Pendente Análise",
    color: "bg-gray-100 text-gray-700 border-gray-200",
    icon: FileText
  },
  aprovar_seguir: {
    label: "Aprovar e Seguir",
    color: "bg-green-100 text-green-700 border-green-200",
    icon: ThumbsUp
  },
  nao_seguir: {
    label: "Não Seguir",
    color: "bg-red-100 text-red-700 border-red-200",
    icon: ThumbsDown
  }
};

/** API pode devolver snake_case ou camelCase; valores desconhecidos tratamos como pendente. */
function getStatusAprovacao(edital) {
  const v = edital?.statusAprovacao ?? edital?.status_aprovacao;
  if (v === "aprovar_seguir" || v === "nao_seguir") return v;
  return "pendente";
}

function getTimestampCriacao(edital) {
  const raw = edital?.created_at ?? edital?.created_date ?? edital?.createdAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Prazo de proposta/entrega exibido na análise: N dias antes da abertura (mesma regra do badge). */
const DIAS_ANTES_ABERTURA_PARA_ENTREGA = 10;

function getDataAberturaDia(edital) {
  const d = parseDataCertame(edital);
  return d ? startOfDay(d) : null;
}

/** Dias até o limite de entrega (abertura − N); menor = mais urgente. null sem data válida. */
function getDiasAtePrazoEntrega(edital) {
  const aberturaDia = getDataAberturaDia(edital);
  if (!aberturaDia) return null;
  const prazoEntrega = addDays(aberturaDia, -DIAS_ANTES_ABERTURA_PARA_ENTREGA);
  return differenceInDays(prazoEntrega, startOfDay(new Date()));
}

function formatLabelDataAbertura(edital) {
  const d = getDataAberturaDia(edital);
  if (!d) return null;
  return format(d, "dd/MM/yyyy", { locale: ptBR });
}

/**
 * Ordena por data de abertura do certame; sem data válida vai ao fim.
 * @param {'recentes' | 'antigos'} modo
 */
function compararPorDataAbertura(a, b, modo) {
  const da = parseDataCertame(a);
  const db = parseDataCertame(b);
  const temA = !!da;
  const temB = !!db;
  if (temA && temB) {
    const ta = startOfDay(da).getTime();
    const tb = startOfDay(db).getTime();
    const diff = modo === "recentes" ? tb - ta : ta - tb;
    if (diff !== 0) return diff;
  } else if (temA && !temB) return -1;
  else if (!temA && temB) return 1;
  return modo === "recentes"
    ? getTimestampCriacao(b) - getTimestampCriacao(a)
    : getTimestampCriacao(a) - getTimestampCriacao(b);
}

/** Prazo de entrega mais urgente primeiro; alinhado ao badge (dias até o limite). */
function compararPorPrazoEntregaUrgente(a, b) {
  const diasA = getDiasAtePrazoEntrega(a);
  const diasB = getDiasAtePrazoEntrega(b);
  if (diasA != null && diasB != null) {
    const diff = diasA - diasB;
    if (diff !== 0) return diff;
  } else if (diasA != null) return -1;
  else if (diasB != null) return 1;
  return getTimestampCriacao(b) - getTimestampCriacao(a);
}

export default function AnalisePage() {
  const queryClient = useQueryClient();
  const [filtroStatus, setFiltroStatus] = useState("pendente");
  const [busca, setBusca] = useState("");
  const [ordenacao, setOrdenacao] = useState("mais_antigos"); // "mais_recentes" | "mais_antigos" | "prazo_urgente" | "alfabetico"
  const [editalSelecionado, setEditalSelecionado] = useState(null);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [dialogAnexosAberto, setDialogAnexosAberto] = useState(false);
  const [editalParaAnexos, setEditalParaAnexos] = useState(null);
  const [novoStatus, setNovoStatus] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [responsavelEmail, setResponsavelEmail] = useState("");
  const [dialogResponsavelAberto, setDialogResponsavelAberto] = useState(false);
  const [editalResponsavelSelecionado, setEditalResponsavelSelecionado] = useState(null);
  const [responsavelDialogEmail, setResponsavelDialogEmail] = useState("");
  const { user } = useAuth();
  const [editaisSelecionados, setEditaisSelecionados] = useState([]);
  const [dialogExclusaoAberto, setDialogExclusaoAberto] = useState(false);
  const [comentarioExclusao, setComentarioExclusao] = useState("");
  const [trashPreviewLoading, setTrashPreviewLoading] = useState(false);
  const [trashPreview, setTrashPreview] = useState(null);
  const [exibidos, setExibidos] = useState(20);
  const [anexosEditalLoading, setAnexosEditalLoading] = useState(false);
  /** Título exibido no modal de anexos enquanto o GET completo carrega */
  const [tituloDialogAnexos, setTituloDialogAnexos] = useState("");

  const isVistaExcluidos = filtroStatus === "excluido";

  useEffect(() => {
    setExibidos(20);
  }, [filtroStatus, busca, ordenacao]);

  useEffect(() => {
    if (isVistaExcluidos) {
      setEditaisSelecionados([]);
    }
  }, [isVistaExcluidos]);

  const getPrazoStatus = React.useCallback((edital) => {
    const aberturaDia = getDataAberturaDia(edital);
    if (!aberturaDia) return null;

    const prazoEntrega = addDays(aberturaDia, -DIAS_ANTES_ABERTURA_PARA_ENTREGA);
    const hoje = startOfDay(new Date());

    const diasRestantes = differenceInDays(prazoEntrega, hoje);

    if (diasRestantes < 0) {
      return {
        cor: 'bg-red-100 text-red-700 border-red-200',
        texto: 'Prazo Vencido',
        prazo: format(prazoEntrega, "dd/MM/yyyy", { locale: ptBR }),
        icon: XCircle
      };
    } else if (diasRestantes === 0) {
      return {
        cor: 'bg-amber-100 text-amber-700 border-amber-200',
        texto: 'Vence Hoje',
        prazo: format(prazoEntrega, "dd/MM/yyyy", { locale: ptBR }),
        icon: Clock
      };
    } else if (diasRestantes <= 3) {
      return {
        cor: 'bg-amber-100 text-amber-700 border-amber-200',
        texto: `Faltam ${diasRestantes} dias`,
        prazo: format(prazoEntrega, "dd/MM/yyyy", { locale: ptBR }),
        icon: Clock
      };
    } else {
      return {
        cor: 'bg-green-100 text-green-700 border-green-200',
        texto: `Faltam ${diasRestantes} dias`,
        prazo: format(prazoEntrega, "dd/MM/yyyy", { locale: ptBR }),
        icon: CheckCircle2
      };
    }
  }, []);

  const { data: analisePayload, isLoading, error } = useQuery({
    queryKey: ['editais-analise', isVistaExcluidos ? 'trashed' : 'active', filtroStatus],
    queryFn: async () => {
      try {
        const filters = { status: 'concluido' };
        if (isVistaExcluidos) {
          filters.deleted_scope = 'only_trashed';
        } else if (
          filtroStatus === 'pendente' ||
          filtroStatus === 'aprovar_seguir' ||
          filtroStatus === 'nao_seguir'
        ) {
          filters.status_aprovacao = filtroStatus;
        }
        return await entities.Edital.listAnaliseResumo('-createdAt', 100, filters);
      } catch (err) {
        console.error("Erro ao carregar editais:", err);
        return {
          items: [],
          stats: { total: 0, pendente: 0, aprovar_seguir: 0, nao_seguir: 0, favoritos: 0 },
        };
      }
    },
    retry: 1,
    staleTime: 60000,
    gcTime: 300000,
    placeholderData: (prev) => prev,
  });

  const editais = analisePayload?.items ?? [];
  const estatisticasApi = analisePayload?.stats;

  const precisaListaUsuarios = dialogAberto || dialogResponsavelAberto;

  const { data: usuariosResponsaveis = [], isLoading: usuariosResponsaveisLoading } = useQuery({
    queryKey: ["usuarios-analise-responsavel"],
    queryFn: () => entities.User.list(),
    enabled: precisaListaUsuarios,
    staleTime: 60000,
  });

  const usuariosResponsaveisAtivos = useMemo(
    () =>
      (Array.isArray(usuariosResponsaveis) ? usuariosResponsaveis : [])
        .filter((u) => u?.ativo !== false && u?.email)
        .sort((a, b) => String(a?.nome || "").localeCompare(String(b?.nome || ""), "pt-BR")),
    [usuariosResponsaveis]
  );

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, observacoes, responsavelEmail: responsavelEmailSelecionado }) => {
      const updateData = {
        status_aprovacao: status,
        data_aprovacao: new Date().toISOString(),
        aprovador: user?.email || '',
        observacoes_aprovacao: observacoes
      };
      
      // Se aprovar e seguir, define coluna_kanban para análise do edital
      if (status === 'aprovar_seguir') {
        const responsavelSelecionado = usuariosResponsaveisAtivos.find(
          (u) => u.email === responsavelEmailSelecionado
        );
        updateData.coluna_kanban = 'analise_edital';
        updateData.responsavel_id = responsavelSelecionado?.id ?? null;
        updateData.responsavel_nome = responsavelSelecionado?.nome ?? null;
        updateData.responsavel_email = responsavelSelecionado?.email ?? null;
      }
      
      await entities.Edital.update(id, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editais-analise'] });
      queryClient.invalidateQueries({ queryKey: ['editais-licitacao'] });
      queryClient.invalidateQueries({ queryKey: ['editais-status-report'] });
      setDialogAberto(false);
      setEditalSelecionado(null);
      setObservacoes("");
      setResponsavelEmail("");
    }
  });

  const toggleOcultarKanbanMutation = useMutation({
    mutationFn: async ({ id, ocultar }) => {
      await entities.Edital.update(id, {
        oculto_kanban: ocultar
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editais-analise'] });
      queryClient.invalidateQueries({ queryKey: ['editais-licitacao'] });
      queryClient.invalidateQueries({ queryKey: ['editais-status-report'] });
    }
  });

  const updateResponsavelMutation = useMutation({
    mutationFn: async ({ id, responsavelEmail: emailSelecionado }) => {
      const responsavelSelecionado = usuariosResponsaveisAtivos.find((u) => u.email === emailSelecionado);
      await entities.Edital.update(id, {
        responsavel_id: responsavelSelecionado?.id ?? null,
        responsavel_nome: responsavelSelecionado?.nome ?? null,
        responsavel_email: responsavelSelecionado?.email ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["editais-analise"] });
      queryClient.invalidateQueries({ queryKey: ["editais-licitacao"] });
      queryClient.invalidateQueries({ queryKey: ["editais-status-report"] });
      setDialogResponsavelAberto(false);
      setEditalResponsavelSelecionado(null);
      setResponsavelDialogEmail("");
      toast.success("Responsável do edital atualizado.");
    },
    onError: (err) => {
      toast.error(getDisplayMessage(err, "Erro ao atualizar responsável do edital."));
    },
  });

  useEffect(() => {
    if (!dialogExclusaoAberto || editaisSelecionados.length === 0) {
      setTrashPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setTrashPreviewLoading(true);
      setTrashPreview(null);
      try {
        const data = await entities.Edital.trashPreview(editaisSelecionados);
        if (cancelled) return;
        const editais = data?.editais ?? [];
        const sum = {
          propostas: 0,
          contratos: 0,
          licoesAprendidas: 0,
          agendaEvents: 0,
          atestadosDeclaracoes: 0,
        };
        let anyBlocked = false;
        let anyMissing = false;
        for (const e of editais) {
          if (e.missing) {
            anyMissing = true;
            continue;
          }
          if (e.blocked) anyBlocked = true;
          sum.propostas += e.counts?.propostas ?? 0;
          sum.contratos += e.counts?.contratos ?? 0;
          sum.licoesAprendidas += e.counts?.licoesAprendidas ?? 0;
          sum.agendaEvents += e.counts?.agendaEvents ?? 0;
          sum.atestadosDeclaracoes += e.counts?.atestadosDeclaracoes ?? 0;
        }
        setTrashPreview({ editais, anyBlocked, anyMissing, sum });
      } catch (err) {
        if (!cancelled) {
          setTrashPreview({
            error: true,
            message: getDisplayMessage(err, "Não foi possível verificar vínculos. Tente novamente."),
          });
        }
      } finally {
        if (!cancelled) setTrashPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogExclusaoAberto, editaisSelecionados]);

  const naoHaEditaisValidosParaExcluir = useMemo(() => {
    if (!trashPreview?.editais || trashPreview.error || trashPreviewLoading) return false;
    return editaisSelecionados.every((id) => {
      const row = trashPreview.editais.find((entry) => entry.id === id);
      return !row || row.missing;
    });
  }, [trashPreview, trashPreviewLoading, editaisSelecionados]);

  const deleteEditaisMutation = useMutation({
    mutationFn: async ({ ids, cascade, motivo_exclusao }) => {
      await Promise.all(
        ids.map((id) => entities.Edital.delete(id, { cascade: !!cascade, motivo_exclusao }))
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['editais-analise'] });
      queryClient.invalidateQueries({ queryKey: ['editais-licitacao'] });
      queryClient.invalidateQueries({ queryKey: ['editais-status-report'] });
      setEditaisSelecionados([]);
      setComentarioExclusao("");
      setDialogExclusaoAberto(false);
    },
    onError: (err) => {
      console.error("Erro ao excluir editais:", err);
      toast.error(getDisplayMessage(err, "Erro ao excluir editais. Tente novamente."), { duration: 5000 });
    },
  });

  const handleAbrirDialog = (edital, status) => {
    setEditalSelecionado(edital);
    setNovoStatus(status);
    setObservacoes(edital.observacoes_aprovacao || "");
    setResponsavelEmail(edital.responsavel_email || edital.responsavelEmail || "");
    setDialogAberto(true);
  };

  const handleConfirmarStatus = () => {
    if (editalSelecionado) {
      updateStatusMutation.mutate({
        id: editalSelecionado.id,
        status: novoStatus,
        observacoes: observacoes,
        responsavelEmail,
      });
    }
  };

  const handleToggleSelecao = (editalId) => {
    setEditaisSelecionados(prev =>
      prev.includes(editalId)
        ? prev.filter(id => id !== editalId)
        : [...prev, editalId]
    );
  };

  const handleSelecionarTodos = () => {
    if (editaisSelecionados.length === editaisFiltrados.length) {
      setEditaisSelecionados([]);
    } else {
      setEditaisSelecionados(editaisFiltrados.map(e => e.id));
    }
  };

  const handleExcluirSelecionados = () => {
    setComentarioExclusao("");
    setDialogExclusaoAberto(true);
  };

  const handleConfirmarExclusao = (e) => {
    e?.preventDefault?.();
    const motivo = comentarioExclusao.trim();
    if (!motivo) {
      toast.error("Informe o motivo da exclusão para manter o histórico claro.");
      return;
    }
    if (trashPreviewLoading || trashPreview?.error) return;
    const idsToDelete =
      trashPreview?.editais && !trashPreview.error
        ? editaisSelecionados.filter((id) => {
            const row = trashPreview.editais.find((entry) => entry.id === id);
            return row && !row.missing;
          })
        : editaisSelecionados;
    if (idsToDelete.length === 0) {
      toast.error("Nenhum edital disponível para exclusão.");
      return;
    }
    const cascade = trashPreview?.anyBlocked === true;
    deleteEditaisMutation.mutate({ ids: idsToDelete, cascade, motivo_exclusao: motivo });
  };

  const handleToggleFavorito = async (edital) => {
    await entities.Edital.update(edital.id, { favorito: !edital.favorito });
    queryClient.invalidateQueries({ queryKey: ['editais-analise'] });
  };

  const handleExcluirEdital = (edital) => {
    setComentarioExclusao("");
    setEditaisSelecionados([edital.id]);
    setDialogExclusaoAberto(true);
  };

  const handleAbrirDialogResponsavel = (edital) => {
    setEditalResponsavelSelecionado(edital);
    setResponsavelDialogEmail(edital.responsavel_email || edital.responsavelEmail || "");
    setDialogResponsavelAberto(true);
  };

  const handleConfirmarResponsavel = () => {
    if (!editalResponsavelSelecionado?.id) return;
    updateResponsavelMutation.mutate({
      id: editalResponsavelSelecionado.id,
      responsavelEmail: responsavelDialogEmail,
    });
  };

  // New functions for managing attachments dialog
  const handleAbrirAnexos = async (edital) => {
    setTituloDialogAnexos(
      String(edital.titulo_licitacao || edital.instituicao || edital.nome_arquivo || "Edital")
    );
    setDialogAnexosAberto(true);
    setEditalParaAnexos(null);
    setAnexosEditalLoading(true);
    try {
      const full = await entities.Edital.get(edital.id);
      setEditalParaAnexos(full);
    } catch (err) {
      console.error(err);
      toast.error(getDisplayMessage(err, "Não foi possível carregar os anexos."));
      setDialogAnexosAberto(false);
    } finally {
      setAnexosEditalLoading(false);
    }
  };

  const invalidateAnaliseLista = () => {
    queryClient.invalidateQueries({ queryKey: ['editais-analise'] });
  };

  const fecharDialogAnexos = () => {
    setDialogAnexosAberto(false);
    setEditalParaAnexos(null);
    setTituloDialogAnexos("");
  };

  // Filtra e ordena editais (memoizado para performance)
  const editaisFiltrados = React.useMemo(() => editais
    .filter(edital => {
      // Filtro de status (excluídos: lista já vem só da lixeira pela API)
      const statusMatch =
        filtroStatus === "excluido"
          ? true
          : filtroStatus === "todos"
            ? true
            : getStatusAprovacao(edital) === filtroStatus;
      
      // Filtro de busca
      if (!busca) return statusMatch;
      
      const termoBusca = busca.toLowerCase();
      const buscaMatch = 
        String(edital.id ?? '').toLowerCase().includes(termoBusca) ||
        edital.nome_arquivo?.toLowerCase().includes(termoBusca) ||
        edital.titulo_licitacao?.toLowerCase().includes(termoBusca) ||
        edital.instituicao?.toLowerCase().includes(termoBusca) ||
        edital.responsavel_nome?.toLowerCase().includes(termoBusca) ||
        edital.responsavelNome?.toLowerCase().includes(termoBusca) ||
        edital.responsavel_email?.toLowerCase().includes(termoBusca) ||
        edital.responsavelEmail?.toLowerCase().includes(termoBusca) ||
        edital.numero_processo?.toLowerCase().includes(termoBusca) ||
        edital.numero_pregao?.toLowerCase().includes(termoBusca) ||
        edital.unidade_compradora_uasg?.toLowerCase().includes(termoBusca) ||
        edital.sistema_eletronico?.toLowerCase().includes(termoBusca) ||
        edital.identificacao?.toLowerCase().includes(termoBusca) ||
        edital.objeto?.toLowerCase().includes(termoBusca) ||
        edital.modalidade?.toLowerCase().includes(termoBusca);
      
      return statusMatch && buscaMatch;
    })
    .sort((a, b) => {
      if (ordenacao === "mais_recentes" || ordenacao === "abertura_recentes") {
        return compararPorDataAbertura(a, b, "recentes");
      }
      if (ordenacao === "mais_antigos" || ordenacao === "abertura_antigos") {
        return compararPorDataAbertura(a, b, "antigos");
      }
      if (ordenacao === "prazo_urgente") {
        return compararPorPrazoEntregaUrgente(a, b);
      }
      if (ordenacao === "alfabetico") {
        const nomeA = (a.titulo_licitacao || a.instituicao || a.nome_arquivo || '').toLowerCase();
        const nomeB = (b.titulo_licitacao || b.instituicao || b.nome_arquivo || '').toLowerCase();
        return nomeA.localeCompare(nomeB);
      }
      return 0;
    }), [editais, filtroStatus, busca, ordenacao]);

  const editaisVisiveis = React.useMemo(
    () => editaisFiltrados.slice(0, exibidos),
    [editaisFiltrados, exibidos]
  );

  const estatisticas = React.useMemo(
    () => ({
      total: estatisticasApi?.total ?? 0,
      pendentes: estatisticasApi?.pendente ?? 0,
      aprovarSeguir: estatisticasApi?.aprovar_seguir ?? 0,
      naoSeguir: estatisticasApi?.nao_seguir ?? 0,
      favoritos: estatisticasApi?.favoritos ?? 0,
    }),
    [estatisticasApi]
  );

  const formatDate = React.useCallback((dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return format(date, "d 'de' MMMM, HH:mm", { locale: ptBR });
    } catch {
      return '';
    }
  }, []);

  if (error) {
    return (
      <div className="p-4 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Erro ao carregar dados. Por favor, recarregue a página ou tente novamente mais tarde.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen" data-tour="tour-Analise-root">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            Análise e Aprovação de Editais
          </h1>
          <p className="text-slate-600">
            Analise e defina o status de cada edital processado
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-tour="tour-Analise-primary">
          <Card className="border-none shadow-md bg-white/90 backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-1">Total</p>
                <p className="text-2xl font-bold text-slate-900">{estatisticas.total}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-gray-50">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900">{estatisticas.pendentes}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-green-50">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-sm text-green-700 mb-1">Aprovar</p>
                <p className="text-2xl font-bold text-green-900">{estatisticas.aprovarSeguir}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-red-50">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-sm text-red-700 mb-1">Não Seguir</p>
                <p className="text-2xl font-bold text-red-900">{estatisticas.naoSeguir}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md bg-amber-50">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-sm text-amber-700 mb-1">Favoritos</p>
                <p className="text-2xl font-bold text-amber-900">{estatisticas.favoritos}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4">
              {/* Campo de Busca */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Buscar por ID, nome, instituição, processo, pregão, objeto..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-12 h-12 text-base border-slate-200 focus:border-blue-500"
                />
              </div>

              {/* Filtros e Ações */}
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-wrap flex-1">
                  <Filter className="w-5 h-5 text-slate-500" />
                  <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                    <SelectTrigger className="w-64">
                      <SelectValue placeholder="Filtrar por status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os Editais</SelectItem>
                      <SelectItem value="pendente">Pendente Análise</SelectItem>
                      <SelectItem value="aprovar_seguir">Aprovar e Seguir</SelectItem>
                      <SelectItem value="nao_seguir">Não Seguir</SelectItem>
                      <SelectItem value="excluido">Excluído</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={ordenacao} onValueChange={setOrdenacao}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mais_recentes">
                        <div className="flex items-center gap-2">
                          <ArrowDown className="w-4 h-4" />
                          Abertura: Mais Recentes Primeiro
                        </div>
                      </SelectItem>
                      <SelectItem value="mais_antigos">
                        <div className="flex items-center gap-2">
                          <ArrowUp className="w-4 h-4" />
                          Abertura: Mais Antigos Primeiro
                        </div>
                      </SelectItem>
                      <SelectItem
                        value="prazo_urgente"
                        title={`Ordena pelo prazo de entrega de propostas (${DIAS_ANTES_ABERTURA_PARA_ENTREGA} dias antes da abertura), igual ao indicador na lista`}
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Prazo de entrega mais urgente
                        </div>
                      </SelectItem>
                      <SelectItem value="alfabetico">
                        <div className="flex items-center gap-2">
                          <ArrowUpDown className="w-4 h-4" />
                          Ordem Alfabética
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {editaisFiltrados.length > 0 &&
                  !isVistaExcluidos &&
                  (user?.role === "admin" || user?.perfil === "admin" || user?.permissoes?.permitir_exclusao_edital !== false) && (
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelecionarTodos}
                      className="whitespace-nowrap"
                    >
                      {editaisSelecionados.length === editaisFiltrados.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </Button>

                    {editaisSelecionados.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleExcluirSelecionados}
                        className="whitespace-nowrap"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir ({editaisSelecionados.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {isLoading ? (
            Array(5).fill(0).map((_, i) => (
              <Card key={i} className="border-none shadow-md animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))
          ) : editaisFiltrados.length === 0 ? (
            <Card className="border-none shadow-md">
              <CardContent className="p-12 text-center">
                <p className="text-slate-500 text-lg">
                  {busca
                    ? 'Nenhum edital encontrado com os critérios de busca'
                    : isVistaExcluidos
                      ? 'Nenhum edital excluído encontrado neste filtro'
                      : 'Nenhum edital encontrado com este filtro'}
                </p>
                {busca && (
                  <Button
                    variant="outline"
                    onClick={() => setBusca("")}
                    className="mt-4"
                  >
                    Limpar Busca
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
            {editaisVisiveis.map((edital) => {
              const statusAtual = getStatusAprovacao(edital);
              const StatusIcon = statusConfig[statusAtual].icon;
              const isSelected = editaisSelecionados.includes(edital.id);
              const prazoInfo = getPrazoStatus(edital);
              const dataFormatada = formatDate(edital.created_date);
              const dataAberturaFormatada = formatLabelDataAbertura(edital);
              const PrazoIcon = prazoInfo?.icon;
              const numAnexos =
                (Array.isArray(edital.anexos_adicionais) ? edital.anexos_adicionais.length : 0) ||
                (typeof edital.anexos_adicionais_count === "number" ? edital.anexos_adicionais_count : 0);
              const valorEstimadoNumero =
                typeof edital.valor_estimado === 'number'
                  ? edital.valor_estimado
                  : null;
              const isSigiloso = edital.valor_sigiloso === true;
              const valorEstimadoTexto = isSigiloso || !valorEstimadoNumero
                ? 'Sigiloso'
                : new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(valorEstimadoNumero);
              const processadoPor = edital.processado_por || edital.created_by || '—';
              const responsavel = edital.responsavel_nome || edital.responsavel_email || 'Não definido';

              return (
                <Card key={edital.id} className={`border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur-sm ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {!isVistaExcluidos &&
                        (user?.role === "admin" || user?.perfil === "admin" || user?.permissoes?.permitir_exclusao_edital !== false) && (
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleToggleSelecao(edital.id)}
                            className="mt-1"
                          />
                        </div>
                      )}

                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg text-slate-900 mb-1">
                              {edital.titulo_licitacao || edital.instituicao || edital.nome_arquivo}
                            </h3>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                              {dataFormatada && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {dataFormatada}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 pl-[52px]">
                          {edital.numero_pregao && edital.numero_pregao !== 'Não localizado' && edital.numero_pregao !== 'null' && (
                            <div>
                              <span className="text-sm text-slate-600">Pregão: </span>
                              <span className="text-sm font-medium text-slate-900">
                                {edital.numero_pregao}
                              </span>
                            </div>
                          )}

                          {edital.sistema_eletronico && edital.sistema_eletronico !== 'Não localizado' && edital.sistema_eletronico !== 'null' && (
                            <div>
                              <span className="text-sm text-slate-600">Sistema: </span>
                              <span className="text-sm font-medium text-slate-900">
                                {edital.sistema_eletronico}
                              </span>
                            </div>
                          )}

                          {edital.identificacao && edital.identificacao !== 'Não localizado' && edital.identificacao !== 'null' && (
                            <div>
                              <span className="text-sm text-slate-600">Identificação: </span>
                              <span className="text-sm font-medium text-slate-900">
                                {edital.identificacao}
                              </span>
                            </div>
                          )}

                          {edital.numero_processo && edital.numero_processo !== 'Não localizado' && edital.numero_processo !== 'null' && (
                            <div>
                              <span className="text-sm text-slate-600">Processo: </span>
                              <span className="text-sm font-medium text-slate-900">
                                {edital.numero_processo}
                              </span>
                            </div>
                          )}
                        </div>

                        {edital.objeto && edital.objeto !== 'Não localizado' && edital.objeto !== 'null' && (
                          <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                            {edital.objeto}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className={`${statusConfig[statusAtual].color} border flex items-center gap-1`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig[statusAtual].label}
                          </Badge>

                          {dataAberturaFormatada && (
                            <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-700">
                              Abertura: {dataAberturaFormatada}
                            </Badge>
                          )}

                          {prazoInfo && (
                            <Badge variant="outline" className={`border flex items-center gap-1 ${prazoInfo.cor}`}>
                              {PrazoIcon && <PrazoIcon className="w-3 h-3" />}
                              Prazo Entrega: {prazoInfo.prazo} ({prazoInfo.texto})
                            </Badge>
                          )}

                          {edital.modalidade && edital.modalidade !== 'Não localizado' && edital.modalidade !== 'null' && (
                            <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-700">
                              {edital.modalidade}
                            </Badge>
                          )}

                          {valorEstimadoTexto && (
                            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                              Valor estimado: {valorEstimadoTexto}
                            </Badge>
                          )}

                          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                            Processado por: {processadoPor}
                          </Badge>

                          <Badge variant="outline" className="border-cyan-200 bg-cyan-50 text-cyan-700">
                            Responsável: {responsavel}
                          </Badge>

                          {numAnexos > 0 && (
                            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 flex items-center gap-1">
                              <Paperclip className="w-3 h-3" />
                              {numAnexos} {numAnexos === 1 ? 'anexo' : 'anexos'}
                            </Badge>
                          )}

                          {edital.favorito && (
                            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 flex items-center gap-1">
                              <Star className="w-3 h-3 fill-amber-500" />
                              Favorito
                            </Badge>
                          )}
                        </div>

                        {edital.observacoes_aprovacao && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <p className="text-xs font-semibold text-slate-700 mb-1">Observações:</p>
                            <p className="text-sm text-slate-600">{edital.observacoes_aprovacao}</p>
                          </div>
                        )}

                        {isVistaExcluidos &&
                          (edital.deleted_at || edital.motivo_exclusao || edital.excluido_por) && (
                          <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50/90 text-sm text-amber-950 space-y-1">
                            {edital.deleted_at && (
                              <p>
                                <span className="font-semibold">Excluído em: </span>
                                {formatDate(edital.deleted_at)}
                              </p>
                            )}
                            {edital.excluido_por && (
                              <p>
                                <span className="font-semibold">Excluído por: </span>
                                {edital.excluido_por}
                              </p>
                            )}
                            {edital.motivo_exclusao && (
                              <p>
                                <span className="font-semibold">Motivo da exclusão: </span>
                                {edital.motivo_exclusao}
                              </p>
                            )}
                          </div>
                        )}

                        {edital.aprovador && edital.data_aprovacao && (
                          <div className="mt-2 text-xs text-slate-500">
                            Analisado por {edital.aprovador} em{' '}
                            {formatDate(edital.data_aprovacao)}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 lg:w-64">
                        <Button
                          variant="outline"
                          disabled={isVistaExcluidos}
                          title={isVistaExcluidos ? 'Indisponível para editais excluídos' : undefined}
                          onClick={() => openPageInNewTab(createResultadosEditalDeepLink(edital.id))}
                          className="w-full"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Visualizar Dados
                        </Button>

                        <Button
                          variant="outline"
                          disabled={isVistaExcluidos}
                          title={isVistaExcluidos ? 'Indisponível para editais excluídos' : undefined}
                          onClick={() => handleAbrirAnexos(edital)}
                          className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          Anexos ({numAnexos})
                        </Button>

                        <Button
                          variant="outline"
                          disabled={isVistaExcluidos}
                          title={isVistaExcluidos ? "Indisponível para editais excluídos" : undefined}
                          onClick={() => handleAbrirDialogResponsavel(edital)}
                          className="w-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                        >
                          <UserCog className="w-4 h-4 mr-2" />
                          Definir Responsável
                        </Button>

                        {!isVistaExcluidos && (
                          <>
                        <div className="border-t border-slate-200 my-2"></div>

                        <Button
                          onClick={() => handleToggleFavorito(edital)}
                          variant="outline"
                          className={`w-full ${edital.favorito ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                        >
                          <Star className={`w-4 h-4 mr-2 ${edital.favorito ? 'fill-amber-500' : ''}`} />
                          {edital.favorito ? 'Favorito' : 'Marcar Favorito'}
                        </Button>

                        {(user?.role === "admin" || user?.perfil === "admin" || user?.permissoes?.permitir_aprovacao_edital !== false) && (
                          <Button
                            onClick={() => handleAbrirDialog(edital, 'aprovar_seguir')}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                          >
                            <ThumbsUp className="w-4 h-4 mr-2" />
                            Aprovar e Seguir
                          </Button>
                        )}

                        <Button
                          onClick={() => handleAbrirDialog(edital, 'nao_seguir')}
                          variant="outline"
                          className="w-full border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <ThumbsDown className="w-4 h-4 mr-2" />
                          Não Seguir
                        </Button>

                        {(user?.role === "admin" || user?.perfil === "admin" || user?.permissoes?.permitir_exclusao_edital !== false) && (
                          <Button
                            onClick={() => handleExcluirEdital(edital)}
                            variant="outline"
                            className="w-full border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Excluir
                          </Button>
                        )}

                        {(user?.role === "admin" || user?.perfil === "admin" || user?.permissoes?.permitir_ocultar_kanban !== false) && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                              toggleOcultarKanbanMutation.mutate({
                                id: edital.id,
                                ocultar: !edital.oculto_kanban,
                              })
                            }
                            className={`w-full ${edital.oculto_kanban ? 'border-slate-500 bg-slate-100 text-slate-800' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                          >
                            {edital.oculto_kanban
                              ? 'Restaurar no Kanban e Status Report'
                              : 'Ocultar do Kanban e do Status Report'}
                          </Button>
                        )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {editaisFiltrados.length > exibidos && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => setExibidos(prev => prev + 20)}
                >
                  Carregar mais ({exibidos} de {editaisFiltrados.length})
                </Button>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statusConfig[novoStatus]?.icon &&
                React.createElement(statusConfig[novoStatus].icon, { className: "w-5 h-5" })
              }
              Confirmar: {statusConfig[novoStatus]?.label}
            </DialogTitle>
            <DialogDescription>
              Você está alterando o status do edital{' '}
              <strong>{editalSelecionado?.titulo_licitacao || editalSelecionado?.instituicao || editalSelecionado?.nome_arquivo}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {novoStatus === "aprovar_seguir" && (
              <div className="space-y-2">
                <Label htmlFor="responsavel-edital">Responsável do edital</Label>
                <Select
                  value={responsavelEmail || "none"}
                  onValueChange={(value) => setResponsavelEmail(value === "none" ? "" : value)}
                >
                  <SelectTrigger id="responsavel-edital">
                    <SelectValue placeholder="Selecione um responsável" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não definido</SelectItem>
                    {usuariosResponsaveisAtivos.map((u) => (
                      <SelectItem key={u.id} value={u.email}>
                        {u.nome || u.email} {u.nome ? `(${u.email})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Este usuário será o responsável por acompanhar o edital em todas as etapas.
                </p>
                {usuariosResponsaveisLoading && (
                  <p className="text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                    Carregando usuários…
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="observacoes">
                Observações {novoStatus === 'nao_seguir' ? '(Obrigatório)' : '(Opcional)'}
              </Label>
              <Textarea
                id="observacoes"
                placeholder="Adicione observações sobre esta decisão..."
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            {editalSelecionado?.numero_processo && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Processo:</strong> {editalSelecionado.numero_processo}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogAberto(false)}
              disabled={updateStatusMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmarStatus}
              disabled={
                updateStatusMutation.isPending ||
                (novoStatus === 'nao_seguir' && !observacoes.trim()) ||
                (novoStatus === 'aprovar_seguir' && usuariosResponsaveisLoading)
              }
              className={
                novoStatus === 'aprovar_seguir'
                  ? 'bg-green-600 hover:bg-green-700'
                  : novoStatus === 'nao_seguir'
                  ? 'bg-red-600 hover:bg-red-700'
                  : novoStatus === 'concluido'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : novoStatus === 'ajustar'
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : ''
              }
            >
              {updateStatusMutation.isPending ? 'Salvando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogAnexosAberto}
        onOpenChange={(open) => {
          setDialogAnexosAberto(open);
          if (!open) {
            setEditalParaAnexos(null);
            setTituloDialogAnexos("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Anexos -{" "}
              {editalParaAnexos?.titulo_licitacao ||
                editalParaAnexos?.instituicao ||
                editalParaAnexos?.nome_arquivo ||
                tituloDialogAnexos}
            </DialogTitle>
            <DialogDescription>
              Gerencie os documentos anexos deste edital
            </DialogDescription>
          </DialogHeader>

          {anexosEditalLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-600">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm">Carregando anexos…</p>
            </div>
          ) : editalParaAnexos ? (
            <AnexosEdital edital={editalParaAnexos} onUpdate={invalidateAnaliseLista} />
          ) : null}

          <DialogFooter>
            <Button onClick={fecharDialogAnexos}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogResponsavelAberto}
        onOpenChange={(open) => {
          setDialogResponsavelAberto(open);
          if (!open) {
            setEditalResponsavelSelecionado(null);
            setResponsavelDialogEmail("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Definir responsável do edital</DialogTitle>
            <DialogDescription>
              Selecione o usuário responsável por acompanhar este edital em todas as etapas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="p-3 rounded-lg border bg-slate-50">
              <p className="text-sm text-slate-600">
                <strong>Edital:</strong>{" "}
                {editalResponsavelSelecionado?.titulo_licitacao ||
                  editalResponsavelSelecionado?.instituicao ||
                  editalResponsavelSelecionado?.nome_arquivo ||
                  "—"}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="responsavel-edital-direto">Responsável</Label>
              <Select
                value={responsavelDialogEmail || "none"}
                onValueChange={(value) => setResponsavelDialogEmail(value === "none" ? "" : value)}
              >
                <SelectTrigger id="responsavel-edital-direto">
                  <SelectValue placeholder="Selecione um responsável" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não definido</SelectItem>
                  {usuariosResponsaveisAtivos.map((u) => (
                    <SelectItem key={u.id} value={u.email}>
                      {u.nome || u.email} {u.nome ? `(${u.email})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Se escolher &quot;Não definido&quot;, o responsável atual será removido.
              </p>
              {usuariosResponsaveisLoading && (
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  Carregando usuários…
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogResponsavelAberto(false)}
              disabled={updateResponsavelMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmarResponsavel}
              disabled={updateResponsavelMutation.isPending || usuariosResponsaveisLoading}
            >
              {updateResponsavelMutation.isPending ? "Salvando..." : "Salvar responsável"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={dialogExclusaoAberto}
        onOpenChange={(open) => {
          setDialogExclusaoAberto(open);
          if (!open) setComentarioExclusao("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  Os {editaisSelecionados.length} edital(is) selecionado(s) serão movidos para a lixeira (podem ser
                  recuperados pelo administrador dentro do prazo de retenção).
                </p>
                {trashPreviewLoading && <p className="text-foreground">Verificando vínculos…</p>}
                {trashPreview?.error && (
                  <p className="text-destructive">{trashPreview.message}</p>
                )}
                {trashPreview && !trashPreview.error && !trashPreviewLoading && (
                  <>
                    {trashPreview.anyMissing && (
                      <p className="text-amber-700 dark:text-amber-500">
                        Alguns itens não foram encontrados ou já não estão disponíveis; eles serão ignorados na
                        exclusão.
                      </p>
                    )}
                    {trashPreview.anyBlocked && (
                      <p className="text-foreground">
                        Existem propostas, contratos ou lições aprendidas vinculados. Ao confirmar, esses registros
                        também serão enviados à lixeira. Eventos da agenda e declarações perderão o vínculo com o
                        edital.
                        <span className="mt-2 block text-sm">
                          Totais: {trashPreview.sum.propostas} proposta(s), {trashPreview.sum.contratos}{" "}
                          contrato(s), {trashPreview.sum.licoesAprendidas} lição(ões) aprendida(s);{" "}
                          {trashPreview.sum.agendaEvents} evento(s) de agenda e{" "}
                          {trashPreview.sum.atestadosDeclaracoes} declaração(ões) serão desvinculados.
                        </span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 px-1">
            <Label htmlFor="analise-motivo-exclusao">Motivo da exclusão (obrigatório)</Label>
            <Textarea
              id="analise-motivo-exclusao"
              placeholder="Descreva o motivo para manter o histórico claro..."
              value={comentarioExclusao}
              onChange={(e) => setComentarioExclusao(e.target.value)}
              className="min-h-[100px]"
              disabled={deleteEditaisMutation.isPending}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteEditaisMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              disabled={
                deleteEditaisMutation.isPending ||
                trashPreviewLoading ||
                Boolean(trashPreview?.error) ||
                naoHaEditaisValidosParaExcluir ||
                !comentarioExclusao.trim()
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteEditaisMutation.isPending ? "Excluindo…" : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}